import React, { useContext } from "react";
import { StudentContext } from "./StudentLayout";
import { BookOpen, CalendarCheck, Award, Wallet, CheckCircle2, Clock, TrendingUp, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

const StudentOverview = () => {
  const { student } = useContext(StudentContext);

  if (!student) {
    return (
      <div className="text-center py-20" data-testid="student-overview-loading">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="mt-4 text-slate-500">Loading your dashboard...</p>
      </div>
    );
  }

  const stats = [
    { label: "Attendance", value: `${student.attendancePercentage || 0}%`, icon: CalendarCheck, color: "blue" },
    { label: "Progress", value: `${student.progress || 0}%`, icon: TrendingUp, color: "emerald" },
    { label: "Payment", value: student.paymentStatus === "paid" ? "Paid" : "Pending", icon: Wallet, color: "amber" },
    { label: "Certificate", value: student.certificateStatus || "Locked", icon: Award, color: "violet" },
  ];

  const colorMap = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
  };

  return (
    <div data-testid="student-overview">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Student Dashboard</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Welcome, {student.fullName?.split(" ")[0]}!</h1>
        <p className="mt-1 text-slate-600">Student ID: <span className="font-mono font-semibold text-slate-900">{student.studentId}</span> · Program: <span className="font-semibold">{student.selectedInternship}</span></p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-all">
            <div className={`w-10 h-10 rounded-lg ${colorMap[s.color]} flex items-center justify-center mb-3`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div className="text-2xl font-heading font-bold text-slate-900">{s.value}</div>
            <div className="text-xs text-slate-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg text-slate-900">Course Progress</h2>
            <Link to="/student/courses" className="text-xs text-blue-600 hover:underline font-semibold">View Course</Link>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{student.selectedInternship}</span>
              <span className="font-bold text-slate-900">{student.progress || 0}%</span>
            </div>
            <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${student.progress || 0}%` }}></div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            <Stat label="Duration" value="120 Hrs" />
            <Stat label="Mode" value="Online" />
            <Stat label="Status" value="Ongoing" />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">Certificate Status</h2>
          {student.certificateStatus === "issued" || student.certificateStatus === "generated" ? (
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-green-900">Certificate Available</div>
                <Link to="/student/certificate" className="text-sm text-green-700 hover:underline">Download now →</Link>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">Certificate Locked</div>
                <div className="text-sm text-amber-800">Complete attendance ≥ 75%, assignments and admin approval.</div>
              </div>
            </div>
          )}

          <div className="mt-4 space-y-2 text-sm">
            <Requirement done={student.paymentStatus === "paid"} text="Payment completed" />
            <Requirement done={(student.attendancePercentage || 0) >= 75} text="Attendance ≥ 75%" />
            <Requirement done={(student.progress || 0) >= 100} text="Course progress complete" />
            <Requirement done={student.certificateStatus === "generated" || student.certificateStatus === "issued"} text="Admin approval" />
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="text-center">
    <div className="font-heading font-bold text-slate-900">{value}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);

const Requirement = ({ done, text }) => (
  <div className="flex items-center gap-2">
    {done ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-slate-400" />}
    <span className={done ? "text-slate-700" : "text-slate-500"}>{text}</span>
  </div>
);

export default StudentOverview;
